import React, {useState, useEffect} from 'react';
import {Table, Button, Input, Radio, Spin, Divider, Drawer, Steps} from 'antd';
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import {HiCreditCard} from "react-icons/hi2";
import {ActionButton, DataTable, PageCard, PageShell, PageToolbar} from "../../components/ui";

const NewAdmissions = () => {
    const { t } = useTranslation();
    const uzs = (value) => `${Number(value).toLocaleString()} ${t('common.uzs')}`;
    const sum = (value) => `${Number(value || 0).toLocaleString()} ${t('common.currency')}`;
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentType, setPaymentType] = useState('cash');
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [sortField, setSortField] = useState('id');
    const [sortOrder, setSortOrder] = useState('descend');
    const [totalOrders, setTotalOrders] = useState(0);
    const [orders, setOrders] = useState([]);
    const [selectedOrders, setSelectedOrders] = useState({});
    const [selectedVisitId, setSelectedVisitId] = useState(null);
    const [selectedVisitType, setSelectedVisitType] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [paidServices, setPaidServices] = useState([]);
    const [paymentResponse, setPaymentResponse] = useState(null);

    const discountPercent = selectedOrders.discount ? selectedOrders.discount.percent : 0;


    const fetchOrdersForVisit = async (visitId, visitType) => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/orders/${visitId}?type=${visitType}`);
            setSelectedOrders(response.data.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching orders for visit:', error);
            setLoading(false);
        }
    };

    const fetchOrders = async (page = 1, pageSize = 30, name = '') => {
        try {
            const response = await axiosInstance.get('/orders', {
                params: {
                    page,
                    pageSize,
                    name,  // Qidiruv matni parametr sifatida yuboriladi
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching orders:', error);
            return [];
        }
    };


    useEffect(() => {
        fetchAndSetOrders();
    }, [currentPage, pageSize]);


    const payForServices = async (visitId, amount, paymentType) => {
        try {
            const response = await axiosInstance.post(`/visit/pay/${visitId}`, {
                amount: parseInt(amount),
                type: paymentType
            });
            const services = selectedOrders.orders.map(order => ({
                service: order.service.name,
                count: order.count,
                amount: order.amount
            }));
            fetchAndSetOrders();
            setPaidServices(services);
            setPaymentResponse(response.data.data);
            setPaymentSuccess(true);
            setCurrentStep(1);
        } catch (error) {
            console.error("Error processing payment:", error);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setCurrentPage(1); // Qidiruvda sahifani boshidan boshlash
        fetchAndSetOrders();
    };

    // Inputni qidiruvni kechiktirib yuborish uchun
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchAndSetOrders();
        }, 500); // 0.5 soniya kechikish
        return () => clearTimeout(timeoutId);
    }, [searchText, currentPage, pageSize, sortField, sortOrder]);

    const handleOpenPayment = (record) => {
        setSelectedVisitId(record.id);
        setSelectedVisitType(record.orderable_type);
        fetchOrdersForVisit(record.orderable_id, record.orderable_type.toLowerCase());
        setIsModalVisible(true);
    };

    const columns = [
        {
            title: t('debtors.orderId'), // Order ID
            dataIndex: 'id',
            key: 'id',
            width: 150,
            ellipsis: true,
            sorter: true,
            render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>,
        },
        {
            title: t('common.patient'),
            dataIndex: ['patient', 'name'],
            key: 'patient_name',
            ellipsis: true,
            render: (name) => <span className="text-sm font-medium text-[#1D2C7B]">{name}</span>,
        },
        {
            title: t('common.code'),
            dataIndex: ['patient', 'code'],
            key: 'patient_code',
            width: 130,
            render: (code) => <span className="text-xs text-slate-500 tabular-nums">{code}</span>,
        },
        {
            title: t('common.amount'),
            dataIndex: 'amount',
            key: 'amount',
            width: 150,
            align: 'right',
            render: (amount) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">
                    {uzs(amount)}
                </span>
            ),
        },
        {
            title: t('debtors.transaction'),
            dataIndex: 'transaction',
            key: 'transaction',
            width: 150,
            align: 'right',
            render: (transaction) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">
                    {uzs(transaction)}
                </span>
            ),
        },
        {
            title: t('debtors.debt'),
            dataIndex: 'credit',
            key: 'credit',
            width: 150,
            align: 'right',
            render: (credit) => (
                <span className="text-sm font-medium text-slate-900 tabular-nums whitespace-nowrap">
                    {uzs(credit)}
                </span>
            ),
        },
        {
            title: t('debtors.pay'),
            key: 'pay',
            width: 132,
            fixed: 'right',
            render: (text, record) => (
                <ActionButton
                    icon={<HiCreditCard size="18"/>}
                    onClick={() => handleOpenPayment(record)}
                >
                    {t('debtors.pay')}
                </ActionButton>
            ),
        }
    ];

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-[#1D2C7B]">
                        {record.patient?.name}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500 tabular-nums">
                        {record.patient?.code}
                    </span>
                </div>
                <ActionButton
                    className="shrink-0"
                    collapseOnMobile={false}
                    icon={<HiCreditCard size="18"/>}
                    onClick={() => handleOpenPayment(record)}
                >
                    {t('debtors.pay')}
                </ActionButton>
            </div>

            <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-2">
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('common.amount')}</span>
                    <span className="block text-sm text-slate-700 tabular-nums">
                        {uzs(record.amount)}
                    </span>
                </div>
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('debtors.transaction')}</span>
                    <span className="block text-sm text-slate-700 tabular-nums">
                        {uzs(record.transaction)}
                    </span>
                </div>
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('debtors.debt')}</span>
                    <span className="block text-sm font-medium text-slate-900 tabular-nums">
                        {uzs(record.credit)}
                    </span>
                </div>
            </div>
        </div>
    );

    const handleDrawerClose = () => {
        setIsModalVisible(false);
        setCurrentStep(0);  // Сброс шага при закрытии модального окна
        setPaymentAmount('');  // Очистить данные
        setPaymentSuccess(false);
    };

    const fetchAndSetOrders = async () => {
        setLoading(true);
        const data = await fetchOrders(currentPage, pageSize, searchText, sortField, sortOrder);
        setOrders(data);
        setLoading(false);
    };


    const printReceipt = () => {
        const printContent = document.getElementById("receipt-content");
        const printWindow = window.open('', '', 'width=600,height=400');
        printWindow.document.write(`<html><head><title>${t('payments.receipt')}</title>`);
        printWindow.document.write('<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/4.16.13/antd.min.css" />'); // Add Ant Design CSS
        printWindow.document.write('<link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">'); // Add Tailwind CSS
        printWindow.document.write('</head><body>');
        printWindow.document.write(printContent.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
    };


    return (
        // `relative` keeps the Drawer (rendered with getContainer={false}) anchored to the page.
        <PageShell className="relative">
            <PageCard>
                <PageToolbar
                    title={t('debtors.title')}
                    count={totalOrders}
                    filters={
                        <Input.Search
                            size="small"
                            placeholder={t('common.searchName')}
                            allowClear
                            enterButton={t('payments.searchBtn')}
                            onSearch={handleSearch}
                            onChange={(e) => setSearchText(e.target.value)}  // Harflar kiritilganda o‘zgarishni kuzatish
                            className="w-full md:!w-[280px]"
                        />
                    }
                />

                <Spin spinning={loading}>
                    <DataTable
                        columns={columns}
                        dataSource={orders}
                        mobileCard={renderMobileCard}
                        scrollX={1100}
                        rowKey="id"
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: totalOrders,
                            showSizeChanger: false,
                            position: ['bottomCenter'],
                            className: 'px-4 pb-3',
                            onChange: (page, pageSize) => {
                                setCurrentPage(page);
                                setPageSize(pageSize);
                                fetchAndSetOrders();
                            },
                        }}
                        loading={loading}
                        onChange={(pagination, filters, sorter) => {
                            setSortField(sorter.field);
                            setSortOrder(sorter.order);
                            fetchAndSetOrders();
                        }}
                    />
                </Spin>
            </PageCard>

            <Drawer
                getContainer={false}
                title={t('payments.payTitle', { id: selectedVisitId })}
                visible={isModalVisible}
                onClose={handleDrawerClose}
                width="100%"
                footer={null}
            >
                <div className="flex flex-col lg:flex-row gap-3 lg:gap-4">
                    <div className="min-w-0 flex-1">
                        <Table
                            size="small"
                            scroll={{x: 720}}
                            className="patients-table"
                            rowClassName="even:bg-gray-50"
                            columns={[
                                {
                                    title: t('debtors.orderId'),
                                    dataIndex: "id",
                                    key: "id",
                                    width: 140,
                                    ellipsis: true,
                                    render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>,
                                },
                                {
                                    title: t('common.service'),
                                    dataIndex: ["service", "name"],
                                    key: "service",
                                    render: (name) => <span className="text-sm text-slate-700">{name}</span>,
                                },
                                {
                                    title: t('common.patient'),
                                    dataIndex: ["patient", "name"],
                                    key: "patient",
                                    render: (name) => (
                                        <span className="text-sm font-medium text-[#1D2C7B]">{name}</span>
                                    ),
                                },
                                {
                                    title: t('common.amount'),
                                    dataIndex: "amount",
                                    key: "amount",
                                    width: 190,
                                    align: 'right',
                                    render: (amount) => {
                                        const originalAmount = amount / (1 - discountPercent / 100);
                                        return discountPercent ? (
                                            <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">
                                                <span className="mr-2 text-slate-400 line-through">
                                                    {uzs(originalAmount)}
                                                </span>
                                                {uzs(amount)}
                                            </span>
                                        ) : (
                                            <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">
                                                {uzs(amount)}
                                            </span>
                                        );
                                    }
                                },
                                {
                                    title: t('common.count'),
                                    dataIndex: "count",
                                    key: "count",
                                    width: 80,
                                    align: 'right',
                                    render: (count) => (
                                        <span className="text-sm text-slate-700 tabular-nums">{count}</span>
                                    ),
                                },
                                {
                                    title: t('common.createdAt'),
                                    dataIndex: "created_at",
                                    key: "created_at",
                                    width: 170,
                                    render: (date) => (
                                        <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
                                            {new Date(date).toLocaleString()}
                                        </span>
                                    ),
                                },
                            ]}
                            dataSource={selectedOrders.orders}
                            rowKey="id"
                            pagination={false}
                        />
                    </div>

                    <aside className="w-full lg:w-[380px] shrink-0 rounded-xl border border-slate-200/90 bg-white p-3 sm:p-4 lg:sticky lg:top-0 lg:self-start">
                        <h3 className="m-0 text-sm font-semibold text-slate-800">{t('payments.visitNumber', { id: selectedVisitId })}</h3>
                        <Divider className="!my-3"/>

                        <Steps
                            size="small"
                            current={currentStep}
                            items={[
                                {title: t('payments.enterPayment')},
                                {title: t('payments.receipt')},
                            ]}
                        />

                        {currentStep === 0 ? (
                            <div className="mt-6">
                                <div className="mb-4">
                                    <h4 className="m-0 mb-2 text-sm font-semibold text-slate-800">{t('payments.invoice')}:</h4>
                                    <p className="mb-1 text-sm text-slate-700">
                                        <strong>{t('payments.left')}:</strong>{' '}
                                        <span className="tabular-nums">{uzs(selectedOrders.debit)}</span>
                                    </p>
                                    <p className="mb-1 text-sm text-slate-700">
                                        <strong>{t('payments.total')}:</strong>{' '}
                                        <span className="tabular-nums">{uzs(selectedOrders.total_amount)}</span>
                                    </p>
                                    <p className="mb-0 text-sm text-slate-700">
                                        <strong>{t('payments.paid')}:</strong>{' '}
                                        <span className="tabular-nums">{uzs(selectedOrders.total_payed)}</span>
                                    </p>
                                </div>
                                <Input
                                    placeholder={t('payments.enterAmount')}
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                    className="mb-4 w-full"
                                />
                                <Radio.Group
                                    onChange={(e) => setPaymentType(e.target.value)}
                                    value={paymentType}
                                >
                                    <Radio value="card">{t('payments.card')}</Radio>
                                    <Radio value="cash">{t('payments.cash')}</Radio>
                                </Radio.Group>
                                <div className="mt-4">
                                    <Button
                                        block
                                        type="primary"
                                        style={{backgroundColor: '#1D2C7B', borderColor: '#1D2C7B'}}
                                        onClick={() =>
                                            payForServices(selectedVisitId, paymentAmount, paymentType)
                                        }
                                    >
                                        {t('payments.pay')}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div id="receipt-content" className="text-left w-full text-sm p-6 overflow-auto">
                                    <div className="text-center">
                                        <h2 className="text-xl font-semibold">OXU MED DENTAL</h2>
                                        <span>{t('common.clinicName')}</span> <br/>
                                        <small>+998 91 831 99 99</small>
                                    </div>
                                    <div className="flex mt-4 text-xs">
                                        <div className="flex-grow">{t('payments.receiptNo')}: <span>{selectedVisitId}</span></div>
                                        <div>{new Date().toLocaleString()}</div>
                                    </div>
                                    <hr className="my-2"/>
                                    <div>
                                        <table className="w-full text-xs">
                                            <thead>
                                            <tr>
                                                <th className="py-1 w-1/12 text-center">#</th>
                                                <th className="py-1 text-left">{t('common.service')}</th>
                                                <th className="py-1 w-2/12 text-center">{t('common.count')}</th>
                                                <th className="py-1 w-3/12 text-right">{t('common.amount')}</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {paidServices.map((service, index) => (
                                                <tr key={index}>
                                                    <td className="py-2 text-center">{index + 1}</td>
                                                    <td className="py-2 text-left">
                                                        <span>{service.service}</span>
                                                        <br/>
                                                        <small>{sum(service.amount)}</small>
                                                    </td>
                                                    <td className="py-2 text-center">{service.count}</td>
                                                    <td className="py-2 text-right">{sum(service.amount * service.count)}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <hr className="my-2"/>
                                    <div>
                                        <div className="flex font-semibold">
                                            <div className="flex-grow">{t('payments.total')}</div>
                                            {selectedOrders.discount ? (
                                                <span>
                            <span style={{textDecoration: 'line-through', color: 'gray', marginRight: '8px'}}>
                                {sum(paymentResponse?.total_amount / (1 - selectedOrders?.discount?.percent / 100) || 0)}
                            </span>
                                                    {sum(paymentResponse?.total_amount || 0)}
                        </span>
                                            ) : (
                                                sum(paymentResponse?.total_amount || 0)
                                            )}
                                        </div>
                                        <div className="flex text-xs">
                                            <div className="flex-grow">{t('payments.paid')}</div>
                                            <div>{sum(paymentAmount)}</div>
                                        </div>
                                        <div className="flex text-xs">
                                            <div className="flex-grow">{t('payments.debit')}</div>
                                            <div>{sum(paymentResponse?.debit || 0)}</div>
                                        </div>
                                        <div className="flex text-xs">
                                            <div className="flex-grow">{t('payments.totalPaid')}</div>
                                            <div>{sum(paymentResponse?.total_payed || 0)}</div>
                                        </div>
                                        <div className="flex text-xs">
                                            <div className="flex-grow">{t('payments.paymentMethod')}</div>
                                            <div>{paymentType === 'cash' ? t('payments.cash') : t('payments.card')}</div>
                                        </div>
                                        <div className="flex text-xs mt-2">
                                            <div className="flex-grow">{t('payments.discount')}</div>
                                            <div>{selectedOrders ? `${selectedOrders?.discount?.percent}%` : t('payments.noDiscount')}</div>
                                        </div>
                                    </div>
                                    <hr className="my-2"/>
                                    <div id="legalcopy" className="text-center">
                                        <p className="legal text-center">
                                            <strong>{t('payments.stayHealthy')}</strong>
                                        </p>
                                    </div>
                                </div>

                                <Button
                                    className="mb-3"
                                    block
                                    type="primary"
                                    style={{backgroundColor: '#1D2C7B', borderColor: '#1D2C7B'}}
                                    onClick={printReceipt}
                                >
                                    {t('admissions.printReceipt')}
                                </Button>
                                <Button
                                    block
                                    type="default"
                                    onClick={handleDrawerClose}
                                >
                                    {t('payments.finish')}
                                </Button>
                            </>
                        )}

                    </aside>
                </div>
            </Drawer>
        </PageShell>
    );
};

export default NewAdmissions;
